import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api.js";
import { Brand } from "../Brand.js";

/** Matches the server's rule, so the first thing somebody hears is not a 400. */
const MIN_LENGTH = 10;

/**
 * Choosing a new password, from the link in the email.
 *
 * The token lives in the query string and is sent straight back; it is never
 * put in the URL of anything that follows, and the page does not sign anyone
 * in — typing the new password once at the sign-in screen proves it is the one
 * they meant.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("Those two passwords are not the same.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : "Could not change the password.");
      setBusy(false);
    }
  }

  if (token === "") {
    return (
      <div className="signin">
        <div style={{ width: 320 }}>
          <div className="error small">That link is incomplete. Ask for a new one.</div>
          <Link className="small" to="/forgot-password">
            Send another reset link
          </Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="signin">
        <div style={{ width: 320 }}>
          <Brand title="Wirral Music Factory" subtitle="Hub" className="brand-auth" />
          <ActionConfirmation variant="success" title="Password changed" description={<>
            Password changed. Anywhere that was already signed in to this account has been signed
            out.
           </>} />
          <Link className="small" to="/">
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="signin">
      <form onSubmit={submit}>
        <Brand title="Wirral Music Factory" subtitle="Hub" className="brand-auth" />

        {error && <div className="error small">{error}</div>}

        <label className="small muted" htmlFor="password">
          New password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="new-password"
          minLength={MIN_LENGTH}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <label className="small muted" htmlFor="confirmation">
          New password again
        </label>
        <input
          id="confirmation"
          type="password"
          autoComplete="new-password"
          minLength={MIN_LENGTH}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          required
        />

        <p className="small muted" style={{ margin: 0 }}>
          At least {MIN_LENGTH} characters.
        </p>

        <button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Change password"}
        </button>
      </form>
    </div>
  );
}
